import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './SearchPage.css';
import { CSVLink } from 'react-csv';
import * as XLSX from 'xlsx';
import editIcon from '../../assets/editIcon.jpg';
import PageHeader from './PageHeader';
import { useForm } from 'react-hook-form';
import { AxiosGet } from '../http/Axios';

const SearchPage = () => {
  const [records, setRecords] = useState([]);
  const [detailedRecords, setDetailedRecords] = useState([]);
  const [noResults, setNoResults] = useState(false);
  const [timeSearch, setTimeSearch] = useState('');
  const [showAgeError, setShowAgeError] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending',
  });

  useEffect(() => {
    document.title = 'LeapFrog Lab Registration Admin Portal';
  }, []);

  const { register, handleSubmit, watch, setValue, reset } = useForm({
    defaultValues: {
      searchGender: 'All',
      minAge: '',
      maxAge: '',
      ageUnit: 'Years',
      emailAddress: '',
      familyName: '',
    },
  });

  const searchGender = watch('searchGender');
  const minAge = watch('minAge');
  const maxAge = watch('maxAge');
  const ageUnit = watch('ageUnit');
  const emailAddress = watch('emailAddress');
  const familyName = watch('familyName');

  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);

  const handleAgeUnitChange = (e) => {
    setValue('ageUnit', e.target.value);
    setValue('minAge', '');
    setValue('maxAge', '');
  };

  /**
   * Fetches family records based on the current search parameters
   * Constructs a query string with all non-empty search parameters
   */
  const handleGetFamilies = async (formData) => {

    const min = formData.minAge ? parseInt(formData.minAge, 10) : null;
    const max = formData.maxAge ? parseInt(formData.maxAge, 10) : null;

    if (min !== null && max !== null && min > max) {
      setShowAgeError(true);
      return;
    }

    const params = new URLSearchParams();

    if (formData.searchGender && formData.searchGender !== 'All') {
      params.append('gender', formData.searchGender);
    }
    if (formData.ageUnit) {
      params.append('ageUOM', formData.ageUnit);
      if (formData.ageUnit === 'Months') {
        setTimeSearch('months');
      } else if (formData.ageUnit === 'Years') {
        setTimeSearch('years');
      } else {
        setTimeSearch('');
      }
    }
    if (formData.minAge) {
      params.append('minAge', formData.minAge);
    }
    if (formData.maxAge) {
      params.append('maxAge', formData.maxAge);
    }
    if (formData.emailAddress) {
      params.append('emailAddress', formData.emailAddress);
    }
    if (formData.familyName) {
      params.append('familyName', formData.familyName);
    }

    try {
      const summaryUrl =
        process.env.REACT_APP_ADMIN_SERVICE +
        `/family-guardian-child/summary?${params.toString()}`;
      const summaryResponse = await AxiosGet(summaryUrl);
      setRecords(summaryResponse);

      const detailUrl =
        process.env.REACT_APP_ADMIN_SERVICE +
        `/family-guardian-child?${params.toString()}`;
      const detailResponse = await AxiosGet(detailUrl);
      setDetailedRecords(detailResponse);

      if ((!summaryResponse || summaryResponse.length === 0) &&
        (!detailResponse || detailResponse.length === 0)) {
        setNoResults(true);
      } else {
        setNoResults(false);
      }
    } catch (err) {
      console.log(err);
      setRecords([]);
      setDetailedRecords([]);
      setNoResults(true);
    }
  };

  const getSearchCriteriaString = () => {
    return [
      searchGender && `Gender: ${searchGender}`,
      ageUnit && `Age Unit: ${ageUnit}`,
      minAge && `Min Age: ${minAge}`,
      maxAge && `Max Age: ${maxAge}`,
      emailAddress && `Email: ${emailAddress}`,
      familyName && `Family Name: ${familyName}`,
    ]
      .filter(Boolean)
      .join(', ');
  };

  const getGroupedDetailData = () => {
    const ageLabel = ageUnit === 'Months' ? 'Child Age (Months)' : 'Child Age (Years)';
    const groupedData = [];
    let lastGuardianKey = null;
    const searchCriteria = getSearchCriteriaString();

    detailedRecords.forEach((record, index) => {
      const guardianKey = `${record.email}-${record.primaryPhone}-${record.guardianFirstName}`;

      const row = {
        'Guardian Email': record.email ?? null,
        'Guardian Phone': record.primaryPhone ?? null,
        'Guardian First Name': record.guardianFirstName ?? null,
        'Guardian Last Name': record.guardianLastName ?? null,
        'Child First Name': record.childFirstName ?? null,
        [ageLabel]: record.childAge ?? null,
        'Child Gender': record.childGender ?? null,
        'Child Hand Preference': record.whichHandId ?? null,
        'Language at Home': record.languageSpokenAtHome ?? null,
        'Zip Code': record.zipCode ?? null,
        'City': record.city ?? null,
        'Referred By': record.referredBy ?? null,
        'Search Criteria': index === 0 ? searchCriteria : null
      };

      if (guardianKey === lastGuardianKey) {
        row['Guardian Email'] = null;
        row['Guardian Phone'] = null;
        row['Guardian First Name'] = null;
      } else {
        lastGuardianKey = guardianKey;
      }

      groupedData.push(row);
    });

    return groupedData;
  };

  const formatSummaryForExport = (record) => {
    const searchCriteriaString = [
      searchGender && `Gender: ${searchGender}`,
      ageUnit && `Age Unit: ${ageUnit}`,
      minAge && `Min Age: ${minAge}`,
      maxAge && `Max Age: ${maxAge}`,
      emailAddress && `Email: ${emailAddress}`,
      familyName && `Family Name: ${familyName}`,
    ]
      .filter(Boolean)
      .join(', ');

    return {
      'Family Name': record.familyName ?? null,
      Guardians: record.allGuardians ?? null,
      Children: record.allChildren ?? null,
      'Search Criteria': searchCriteriaString || null,
    };


  };

  const formatDetailForExport = (record) => {

    const ageLabel = ageUnit === 'Months' ? 'Child Age (Months)' : 'Child Age (Years)';
    return {
      'Guardian Email': record.email ?? null,
      'Guardian Phone': record.primaryPhone ?? null,
      'Guardian First Name': record.guardianFirstName ?? null,
      'Guardian Last Name': record.guardianLastName ?? null,
      'Child First Name': record.childFirstName ?? null,
      [ageLabel]: record.childAge ?? null,
      'Child Gender': record.childGender ?? null,
      'Child Hand Preference': record.whichHandId ?? null,
      'Language at Home': record.languageSpokenAtHome ?? null,
      'Zip Code': record.zipCode ?? null,
      'City': record.city ?? null,
      'Referred By': record.referredBy ?? null,
      'Search Criteria': getSearchCriteriaString() || null,
    };
  };

  const exportSummaryToExcel = () => {
    const formattedData = records.map(formatSummaryForExport);
    const ws = XLSX.utils.json_to_sheet(formattedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Summary');
    XLSX.writeFile(wb, 'families_summary.xlsx');
  };

  const exportDetailToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(getGroupedDetailData());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Details');
    XLSX.writeFile(wb, 'families_detail.xlsx');
  };

  const handleResetParameters = () => {
    reset({
      searchGender: 'All',
      minAge: '',
      maxAge: '',
      ageUnit: 'Years',
      emailAddress: '',
      familyName: '',
    });

    setRecords([]);
    setNoResults(false);
  };

  const handleEditRecord = async (guid) => {
    const familyDetailsPath = '/family-details-page';
    navigate(
      `${familyDetailsPath}?guid=${guid}`
    );
  };

  /**
   * Helper function to format record data for export
   * This function standardizes the formatting of record data for both Excel and CSV exports
   * It also handles null/undefined values by replacing them with 'N/A'
   *
   * @param {Object} record - The record to format
   * @returns {Object} - Formatted record with standardized field names and values
   */
  const formatRecordForExport = (record) => {
    const searchCriteriaString = [
      searchGender && `Gender: ${searchGender}`,
      ageUnit && `Age Unit: ${ageUnit}`,
      minAge && `Min Age: ${minAge}`,
      maxAge && `Max Age: ${maxAge}`,
      emailAddress && `Email: ${emailAddress}`,
      familyName && `Family Name: ${familyName}`,
    ]
      .filter(Boolean)
      .join(', ');


    return {
      'Family Name': record.familyName ?? null,
      Guardians: record.allGuardians ?? null,
      Children: record.allChildren ?? null,
      'Search Criteria': searchCriteriaString || null,
    };
  };

  const exportToExcel = () => {
    const formattedData = records.map(formatRecordForExport);
    const ws = XLSX.utils.json_to_sheet(formattedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Families');
    XLSX.writeFile(wb, 'families.xlsx');
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedRecords = useMemo(() => {
    let sortableRecords = [...records];
    if (sortConfig.key) {
      sortableRecords.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableRecords;
  }, [records, sortConfig]);

  const ageOptions =
    ageUnit === 'Months'
      ? [...Array(36).keys()].map((age) => age + 1)
      : ageUnit === 'Years'
        ? [0, ...[...Array(9).keys()].map((age) => age + 1)]
        : [];

  return (
    <div className="dashboard search-page">
      <PageHeader title="Search Family and Children" />

      <main className="page-content">
        <form
          className="search-form"
          onSubmit={handleSubmit(handleGetFamilies)}
        >
          <div className="form-group">
            <label htmlFor="age-range-select">Ages:</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  value="Years"
                  {...register('ageUnit')}
                  checked={ageUnit === 'Years'}
                  onChange={(e) => {
                    if (e.target.checked) {handleAgeUnitChange(e);}
                  }}
                  defaultChecked
                />
                In Years
              </label>
              <label>
                <input
                  type="radio"
                  value="Months"
                  {...register('ageUnit')}
                  checked={ageUnit === 'Months'}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleAgeUnitChange(e);
                    }
                  }}
                />
                In Months
              </label>
            </div>
          </div>

          <div className="form-row age">
            <div className="form-group">
              <label htmlFor="min-age-select">Minimum Age:</label>
              <select
                id="min-age-select"
                className="form-control"
                disabled={!ageUnit}
                {...register('minAge')}
              >
                <option value="">
                  {ageUnit === 'Years'
                    ? 'In years'
                    : ageUnit === 'Months'
                      ? 'In months'
                      : 'Min Age'}
                </option>
                {ageOptions.map((age) => (
                  <option key={age} value={age}>
                    {age}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="max-age-select">Maximum Age:</label>
              <select
                id="max-age-select"
                className="form-control"
                disabled={!ageUnit}
                {...register('maxAge')}
              >
                <option value="">
                  {ageUnit === 'Years'
                    ? 'In years'
                    : ageUnit === 'Months'
                      ? 'In months'
                      : 'Max Age'}
                </option>
                {ageOptions.map((age) => (
                  <option key={age} value={age}>
                    {age}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="gender-select">Gender:</label>
              <select
                id="gender-select"
                className="form-control"
                {...register('searchGender')}
              >
                <option value="All">All</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
                <option value="Decline to state">Decline to state</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email-input">Email Address:</label>
            <input
              id="email-input"
              type="text"
              className="form-control"
              {...register('emailAddress')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="family-name-input">Family Name:</label>
            <input
              id="family-name-input"
              type="text"
              className="form-control"
              {...register('familyName')}
            />
          </div>

          <div className="button-group">
            <button type="submit" className="btn btn-primary">
              Get Families
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleResetParameters}
            >
              Reset Parameters
            </button>
          </div>
        </form>
        {noResults && (
          <div className="no-results-message">
            No results found
          </div>
        )}

        {!noResults && records.length > 0 && (
          <div className="results-container">

            <table className="results-table">
              <thead>
                <tr>
                  <th>Edit</th>
                  <th>Family Name</th>
                  <th>Guardians</th>
                  <th>Children</th>
                  <th>Search Criteria</th>
                </tr>
              </thead>
              <tbody>
                {sortedRecords.map((record, index) => (
                  <tr key={index}>
                    <td>
                      <a
                        href="#"
                        className="edit-record-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handleEditRecord(record.guid);
                        }}
                      >
                        <img src={editIcon} alt="Edit" className="edit-icon" />
                      </a>
                    </td>
                    <td>{record.familyName || 'N/A'}</td>
                    <td>{record.allGuardians || 'N/A'}</td>
                    <td>{record.allChildren || 'N/A'}</td>
                    <td>
                      {[
                        searchGender && `Gender: ${searchGender}`,
                        ageUnit && `Age Unit: ${ageUnit}`,
                        minAge && `Min Age: ${minAge}`,
                        maxAge && `Max Age: ${maxAge}`,
                        emailAddress && `Email: ${emailAddress}`,
                        familyName && `Family Name: ${familyName}`,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="export-options">
              <button className="btn btn-primary" onClick={exportDetailToExcel}>
                Export Detail to Excel
              </button>
              <CSVLink
                data={getGroupedDetailData()}
                filename="families_detail.csv"
                className="btn btn-secondary"
              >
                Export Detail to CSV
              </CSVLink>
            </div>
          </div>
        )}
        {showAgeError && (
          <div className="modal-overlay">
            <div className="modal-box">
              <h3>Invalid Age Range</h3>
              <p>Minimum Age cannot be greater than Maximum Age. Please enter valid values.</p>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setValue('minAge', '');
                  setValue('maxAge', '');
                  setShowAgeError(false);
                }}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </main>
    </div>



  );
};

export default SearchPage;
